import {
    Badge,
    Box,
    Center,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    Select,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Textarea,
    Th,
    Thead,
    Tooltip,
    Tr,
} from '@chakra-ui/react';
import { useMemo, useState, Fragment } from 'react';
import moment from 'moment';
import {
    BusinessLine,
    InventoryAdjustmentCreateType,
    InventoryAdjustmentType,
    InventoryItemEntry,
    InventoryStockAdjustmentEntry,
} from '../../types/inventory';
import { useGetInventoryStockAdjustments } from '../../hooks/inventory/useGetInventoryStockAdjustments';
import { useGetSettings } from '../../hooks/useGetSettings';
import {
    formatBusinessLineDisplay,
    formatStockAdjustmentRoDisplay,
} from '../../utils/inventoryConstants';
import { formatPrice } from '../../functions';
import CurrencyInput from './CurrencyInput';
import QuantityInputField from './QuantityInputField';
import RoNumberField from './RoNumberField';
import { Button, Icon, IconButton, UnderlineTabs } from '../../ui';

const COLLAPSED_LIMIT = 5;
const EXPANDED_LIMIT = 100;
const NOTE_MAX_LENGTH = 1000;

type AdjustmentTypeFilter = 'all' | InventoryAdjustmentType;

const TYPE_FILTERS: Array<{ value: AdjustmentTypeFilter; label: string }> = [
    { value: 'all', label: 'All' },
    { value: 'order', label: 'Order' },
    { value: 'usage', label: 'Usage' },
    { value: 'sale', label: 'Sale' },
    { value: 'initial', label: 'Initial' },
];

export { COLLAPSED_LIMIT };

export interface PendingStockAdjustmentCreate {
    /** Client-only id for list keying / discard. */
    clientId: string;
    type: InventoryAdjustmentCreateType;
    quantity: number;
    businessLine: BusinessLine;
    roNumber?: string;
    unitCost?: number;
    note?: string;
}

/** Staged note edits for persisted adjustments. Empty string clears the note. */
export type PendingStockAdjustmentNoteUpdates = Record<string, string>;

export function createPendingAdjustmentId(): string {
    return `pending-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function getActorName(adj: InventoryStockAdjustmentEntry): string {
    if (adj.userName) {
        return `${adj.userName.firstName} ${adj.userName.lastName}`.trim();
    }
    return 'System';
}

function formatShortActorName(adj: InventoryStockAdjustmentEntry): string {
    const first = adj.userName?.firstName?.trim() ?? '';
    const last = adj.userName?.lastName?.trim() ?? '';
    if (first && last) return `${first.charAt(0)}. ${last}`;
    return getActorName(adj);
}

/** Order and initial add stock; usage and sale draw it down. */
function isInboundType(type: InventoryAdjustmentType): boolean {
    return type === 'order' || type === 'initial';
}

function getAdjustmentTypeMeta(type: InventoryAdjustmentType) {
    if (type === 'order') {
        return { label: 'Order', colorScheme: 'green' as const };
    }
    if (type === 'sale') {
        return { label: 'Sale', colorScheme: 'blue' as const };
    }
    if (type === 'initial') {
        return { label: 'Initial', colorScheme: 'purple' as const };
    }
    return { label: 'Usage', colorScheme: 'orange' as const };
}

function formatUnitCost(adj: { unitCost?: number }): string {
    return adj.unitCost != null ? formatPrice(adj.unitCost) : '—';
}

function normalizeNote(value: string): string {
    return value.trim().slice(0, NOTE_MAX_LENGTH);
}

/** Newest-first: delete only on the top row, or the next row when the one above is pending delete. */
function canRevealDeleteAction(
    index: number,
    adjustments: InventoryStockAdjustmentEntry[],
    pendingDeleteIds: string[],
): boolean {
    if (index < 0 || index >= adjustments.length) return false;
    if (pendingDeleteIds.includes(adjustments[index]._id)) return true;
    if (index === 0) return true;
    return pendingDeleteIds.includes(adjustments[index - 1]._id);
}

type ExpandedNoteTarget =
    | { kind: 'pending'; clientId: string }
    | { kind: 'persisted'; adjustmentId: string }
    | null;

interface StockAdjustmentNoteEditorProps {
    value: string;
    onChange: (value: string) => void;
    onDone: () => void;
    onCancel: () => void;
}

/** Expandable note editor for history-row add/edit. */
function StockAdjustmentNoteEditor({
    value,
    onChange,
    onDone,
    onCancel,
}: StockAdjustmentNoteEditorProps) {
    return (
        <Box mt={2}>
                <Textarea
                    size="sm"
                    value={value}
                    onChange={(e) => onChange(e.target.value.slice(0, NOTE_MAX_LENGTH))}
                    placeholder="Add a note..."
                    rows={1}
                    resize="none"
                    maxLength={NOTE_MAX_LENGTH}
                />
            <HStack spacing={2} mt={2} justify="flex-end">
                <Button size="sm" variant="outlineGray" onClick={onCancel}>
                    Cancel
                </Button>
                <Button size="sm" onClick={onDone}>
                    Done
                </Button>
            </HStack>
        </Box>
    );
}

interface InventoryStockAdjustmentsSectionProps {
    inventoryItemId: string;
    item: InventoryItemEntry;
    enabled?: boolean;
    pendingCreates: PendingStockAdjustmentCreate[];
    pendingDeleteIds: string[];
    pendingNoteUpdates: PendingStockAdjustmentNoteUpdates;
    onPendingCreatesChange: (drafts: PendingStockAdjustmentCreate[]) => void;
    onPendingDeleteIdsChange: (ids: string[]) => void;
    onPendingNoteUpdatesChange: (updates: PendingStockAdjustmentNoteUpdates) => void;
}

const InventoryStockAdjustmentsSection = ({
    inventoryItemId,
    item,
    enabled = true,
    pendingCreates,
    pendingDeleteIds,
    pendingNoteUpdates,
    onPendingCreatesChange,
    onPendingDeleteIdsChange,
    onPendingNoteUpdatesChange,
}: InventoryStockAdjustmentsSectionProps) => {
    const [expanded, setExpanded] = useState(false);
    const [typeFilter, setTypeFilter] = useState<AdjustmentTypeFilter>('all');
    const [isAdding, setIsAdding] = useState(false);
    const [adjustmentType, setAdjustmentType] = useState<InventoryAdjustmentCreateType>('order');
    const [quantity, setQuantity] = useState(0);
    const [unitCost, setUnitCost] = useState(0);
    const [businessLineValue, setBusinessLineValue] = useState('');
    const [roNumberValue, setRoNumberValue] = useState('');
    const [addNote, setAddNote] = useState('');
    const [businessLineError, setBusinessLineError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [expandedNote, setExpandedNote] = useState<ExpandedNoteTarget>(null);
    const [noteDraft, setNoteDraft] = useState('');

    const { data: settingsData } = useGetSettings();
    const businessLineOptions = useMemo(
        () => settingsData?.inventory?.businessLines ?? [],
        [settingsData?.inventory?.businessLines],
    );
    const defaultBusinessLine = settingsData?.inventory?.defaultBusinessLine ?? '';

    const limit = expanded || typeFilter !== 'all' ? EXPANDED_LIMIT : COLLAPSED_LIMIT;
    const { data, isLoading, isError } = useGetInventoryStockAdjustments(
        inventoryItemId,
        limit,
        0,
        enabled,
    );

    const adjustments = useMemo(() => {
        const list = data?.adjustments ?? [];
        return [...list].sort(
            (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
        );
    }, [data?.adjustments]);
    const totalCount = data?.totalCount ?? 0;

    const showMoneyField = adjustmentType === 'order' || adjustmentType === 'sale';
    const moneyFieldLabel = 'Unit Cost';

    const resetAddForm = () => {
        setIsAdding(false);
        setAdjustmentType('order');
        setQuantity(0);
        setUnitCost(item.averageUnitCost);
        setBusinessLineValue('');
        setRoNumberValue('');
        setAddNote('');
        setBusinessLineError(null);
        setFormError(null);
    };

    const resolvedDefaultBusinessLine = useMemo(() => {
        const names = businessLineOptions.map((o) => o.name);
        if (defaultBusinessLine && names.includes(defaultBusinessLine)) {
            return defaultBusinessLine;
        }
        return '';
    }, [businessLineOptions, defaultBusinessLine]);

    const openAddForm = () => {
        setUnitCost(item.averageUnitCost);
        setAdjustmentType('order');
        setQuantity(0);
        setBusinessLineValue(resolvedDefaultBusinessLine);
        setRoNumberValue('');
        setAddNote('');
        setBusinessLineError(null);
        setFormError(null);
        setExpandedNote(null);
        setIsAdding(true);
    };

    const handleStageCreate = () => {
        if (quantity <= 0) {
            setFormError('Enter a quantity greater than zero.');
            return;
        }
        if (showMoneyField && unitCost < 0) {
            setFormError('Unit cost must be zero or greater.');
            return;
        }
        const businessLine = businessLineValue.trim();
        if (!businessLine) {
            setBusinessLineError('Business line is required.');
            return;
        }
        setBusinessLineError(null);
        setFormError(null);
        const roNumber = roNumberValue.trim();
        const note = normalizeNote(addNote);
        onPendingCreatesChange([
            ...pendingCreates,
            {
                clientId: createPendingAdjustmentId(),
                type: adjustmentType,
                quantity,
                businessLine,
                ...(roNumber ? { roNumber } : {}),
                ...(showMoneyField ? { unitCost } : {}),
                ...(note ? { note } : {}),
            },
        ]);
        resetAddForm();
    };

    const discardPendingCreate = (clientId: string) => {
        onPendingCreatesChange(pendingCreates.filter((d) => d.clientId !== clientId));
        if (expandedNote?.kind === 'pending' && expandedNote.clientId === clientId) {
            setExpandedNote(null);
            setNoteDraft('');
        }
    };

    const togglePendingDelete = (adjustmentId: string) => {
        const index = adjustments.findIndex((adj) => adj._id === adjustmentId);
        if (index < 0) return;

        if (pendingDeleteIds.includes(adjustmentId)) {
            onPendingDeleteIdsChange(
                pendingDeleteIds.filter((id) => {
                    const idIndex = adjustments.findIndex((adj) => adj._id === id);
                    return idIndex >= 0 && idIndex < index;
                }),
            );
            return;
        }

        if (!canRevealDeleteAction(index, adjustments, pendingDeleteIds)) return;
        onPendingDeleteIdsChange([...pendingDeleteIds, adjustmentId]);
    };

    const effectivePendingNote = (draft: PendingStockAdjustmentCreate): string =>
        draft.note ?? '';

    const effectivePersistedNote = (adj: InventoryStockAdjustmentEntry): string => {
        if (Object.prototype.hasOwnProperty.call(pendingNoteUpdates, adj._id)) {
            return pendingNoteUpdates[adj._id] ?? '';
        }
        return adj.note ?? '';
    };

    const openPendingNote = (draft: PendingStockAdjustmentCreate) => {
        setIsAdding(false);
        setExpandedNote({ kind: 'pending', clientId: draft.clientId });
        setNoteDraft(effectivePendingNote(draft));
    };

    const openPersistedNote = (adj: InventoryStockAdjustmentEntry) => {
        setIsAdding(false);
        setExpandedNote({ kind: 'persisted', adjustmentId: adj._id });
        setNoteDraft(effectivePersistedNote(adj));
    };

    const closeNoteEditor = () => {
        setExpandedNote(null);
        setNoteDraft('');
    };

    const handleNoteDone = () => {
        if (!expandedNote) return;
        const next = normalizeNote(noteDraft);
        if (expandedNote.kind === 'pending') {
            onPendingCreatesChange(
                pendingCreates.map((d) => {
                    if (d.clientId !== expandedNote.clientId) return d;
                    if (!next) {
                        const { note: _removed, ...rest } = d;
                        void _removed;
                        return rest;
                    }
                    return { ...d, note: next };
                }),
            );
        } else {
            const adj = adjustments.find((a) => a._id === expandedNote.adjustmentId);
            const original = adj?.note ?? '';
            const nextUpdates = { ...pendingNoteUpdates };
            if (next === original) {
                delete nextUpdates[expandedNote.adjustmentId];
            } else {
                nextUpdates[expandedNote.adjustmentId] = next;
            }
            onPendingNoteUpdatesChange(nextUpdates);
        }
        closeNoteEditor();
    };

    const handleNoteCancel = () => {
        closeNoteEditor();
    };

    const canExpand = typeFilter === 'all' && totalCount > COLLAPSED_LIMIT;
    const hasAnyRows = pendingCreates.length > 0 || adjustments.length > 0;
    const matchesFilter = (type: InventoryAdjustmentType) =>
        typeFilter === 'all' || typeFilter === type;
    const visiblePendingCreates = pendingCreates.filter((d) => matchesFilter(d.type));
    const hasVisibleRows =
        visiblePendingCreates.length > 0 || adjustments.some((adj) => matchesFilter(adj.type));

    const renderNoteCell = (
        hasNote: boolean,
        isOpen: boolean,
        onToggle: () => void,
    ) => (
        <Tooltip label="Add Note" hasArrow>
            <IconButton
                aria-label={hasNote ? 'Edit note' : 'Add note'}
                icon={
                    <Icon
                        name="edit"
                        size="sm"
                        color={hasNote ? 'brand.solid' : 'fg.muted'}
                    />
                }
                size="xs"
                variant="ghost"
                colorScheme="gray"
                onClick={onToggle}
                aria-expanded={isOpen}
            />
        </Tooltip>
    );

    const renderDeleteCell = (opts: {
        show: boolean;
        onClick: () => void;
        label: string;
        isUndo?: boolean;
    }) => {
        if (!opts.show) return null;
        return (
            <IconButton
                aria-label={opts.label}
                icon={
                    <Icon
                        name="trash"
                        size="sm"
                        color={opts.isUndo ? 'fg.muted' : 'error.fg'}
                    />
                }
                size="xs"
                variant="ghost"
                colorScheme="gray"
                onClick={opts.onClick}
            />
        );
    };

    const renderChange = (type: InventoryAdjustmentType, quantity: number) => {
        const inbound = isInboundType(type);
        return (
            <Text
                fontSize="sm"
                fontWeight="semibold"
                color={inbound ? 'success.fg' : 'fg.default'}
                whiteSpace="nowrap"
            >
                {inbound ? '+' : '−'}
                {quantity}
            </Text>
        );
    };

    const renderBusinessLineCell = (businessLine: string) => {
        const line = formatBusinessLineDisplay(businessLine) || '—';
        return (
            <Tooltip label={line} placement="top">
                <Text fontSize="sm" color="fg.default" noOfLines={1}>
                    {line}
                </Text>
            </Tooltip>
        );
    };

    const renderRoCell = (roNumber?: string) => (
        <Text fontSize="sm" color="fg.default" noOfLines={1}>
            {formatStockAdjustmentRoDisplay(roNumber)}
        </Text>
    );

    const colCount = 9;

    return (
        <Box>
            <Flex justify="space-between" align="center" mb={2} gap={3}>
                <HStack spacing={2}>
                    <Text fontSize="lg" fontWeight="bold" color="fg.default">
                        Stock Adjustment History
                    </Text>
                    <Box
                        as="span"
                        fontSize="xs"
                        fontWeight="semibold"
                        color="fg.muted"
                        bg="bg.subtle"
                        borderRadius="full"
                        px={2}
                        py={0.5}
                        minW="1.5rem"
                        textAlign="center"
                    >
                        {totalCount}
                    </Box>
                </HStack>
                {!isAdding && (
                    <Button
                        size="sm"
                        leftIcon={<Icon name="plus" size="xs" />}
                        onClick={openAddForm}
                    >
                        Adjust stock
                    </Button>
                )}
            </Flex>

            <UnderlineTabs
                items={TYPE_FILTERS}
                value={typeFilter}
                onChange={setTypeFilter}
                mx={-6}
                px={6}
                mb={4}
            />

            {isAdding && (
                <Box
                    mb={4}
                    p={3}
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor="border.default"
                    overflow="hidden"
                >
                    <Flex mb={3} align="flex-start" overflow="hidden" flexWrap="wrap" gap={3}>
                        <Box
                            overflow="hidden"
                            flexShrink={0}
                            maxW={showMoneyField ? '180px' : '0px'}
                            transition="max-width 0.28s ease"
                            pointerEvents={showMoneyField ? 'auto' : 'none'}
                            aria-hidden={!showMoneyField}
                        >
                            <Box
                                minW="160px"
                                opacity={showMoneyField ? 1 : 0}
                                transform={
                                    showMoneyField ? 'translateX(0)' : 'translateX(-100%)'
                                }
                                transition="opacity 0.22s ease, transform 0.28s ease"
                            >
                                <FormControl>
                                    <FormLabel fontSize="sm">{moneyFieldLabel}</FormLabel>
                                    <CurrencyInput
                                        value={unitCost}
                                        onChange={setUnitCost}
                                        useThousandsSeparator
                                    />
                                </FormControl>
                            </Box>
                        </Box>
                        <FormControl flex={1} minW="100px">
                            <FormLabel fontSize="sm">Quantity</FormLabel>
                            <QuantityInputField
                                value={quantity}
                                onChange={(val) => setQuantity(val ?? 0)}
                            />
                        </FormControl>
                        <FormControl flex={1} minW="140px" isInvalid={!!businessLineError}>
                            <FormLabel fontSize="sm">Business Line</FormLabel>
                            <Select
                                size="sm"
                                placeholder="Select business line"
                                value={businessLineValue}
                                onChange={(e) => {
                                    setBusinessLineValue(e.target.value);
                                    setBusinessLineError(null);
                                }}
                            >
                                {businessLineOptions.map((opt) => (
                                    <option key={opt.name} value={opt.name}>
                                        {opt.name}
                                    </option>
                                ))}
                            </Select>
                            <FormErrorMessage>{businessLineError}</FormErrorMessage>
                        </FormControl>
                        <FormControl flex={1} minW="120px">
                            <FormLabel fontSize="sm">RO #</FormLabel>
                            <RoNumberField value={roNumberValue} onChange={setRoNumberValue} />
                        </FormControl>
                    </Flex>
                    <FormControl mb={0}>
                        <FormLabel fontSize="sm">Note</FormLabel>
                        <Textarea
                            size="sm"
                            value={addNote}
                            onChange={(e) =>
                                setAddNote(e.target.value.slice(0, NOTE_MAX_LENGTH))
                            }
                            placeholder="Add a note..."
                            rows={1}
                            resize="none"
                            maxLength={NOTE_MAX_LENGTH}
                        />
                    </FormControl>
                    {formError && (
                        <Text fontSize="sm" color="error.fg" mb={2} mt={2}>
                            {formError}
                        </Text>
                    )}
                    <Flex justify="space-between" align="center" flexWrap="wrap" gap={3} mt={3}>
                        <HStack spacing={0} bg="bg.muted" borderRadius="md" p="2px">
                            {(
                                [
                                    { value: 'order' as const, label: 'Order' },
                                    { value: 'usage' as const, label: 'Usage' },
                                    { value: 'sale' as const, label: 'Sale' },
                                ] as const
                            ).map((option) => {
                                const isActive = adjustmentType === option.value;
                                return (
                                    <Box
                                        key={option.value}
                                        as="button"
                                        type="button"
                                        px={3}
                                        py={1}
                                        borderRadius="md"
                                        bg={isActive ? 'bg.surface' : 'transparent'}
                                        boxShadow={isActive ? 'sm' : 'none'}
                                        fontSize="sm"
                                        fontWeight="medium"
                                        color={isActive ? 'fg.default' : 'fg.muted'}
                                        transition="all 0.15s ease"
                                        _hover={!isActive ? { color: 'fg.default' } : undefined}
                                        onClick={() => {
                                            if (!isActive) {
                                                setAdjustmentType(option.value);
                                                if (
                                                    option.value === 'order' ||
                                                    option.value === 'sale'
                                                ) {
                                                    setUnitCost(item.averageUnitCost);
                                                }
                                            }
                                        }}
                                    >
                                        {option.label}
                                    </Box>
                                );
                            })}
                        </HStack>
                        <HStack spacing={2}>
                            <Button size="sm" variant="outlineGray" onClick={resetAddForm}>
                                Cancel
                            </Button>
                            <Button size="sm" onClick={handleStageCreate}>
                                Add
                            </Button>
                        </HStack>
                    </Flex>
                </Box>
            )}

            {isLoading && (
                <Center py={8}>
                    <Spinner size="md" color="brand.solid" />
                </Center>
            )}
            {isError && (
                <Text fontSize="sm" color="error.fg" py={4}>
                    Failed to load stock adjustments
                </Text>
            )}
            {!isLoading && !isError && !hasAnyRows && (
                <Text fontSize="sm" color="fg.muted" fontStyle="italic" py={2}>
                    No adjustments yet
                </Text>
            )}
            {!isLoading && !isError && hasAnyRows && !hasVisibleRows && (
                <Text fontSize="sm" color="fg.muted" fontStyle="italic" py={2}>
                    No {TYPE_FILTERS.find((f) => f.value === typeFilter)?.label.toLowerCase()}{' '}
                    adjustments
                </Text>
            )}
            {!isLoading && !isError && hasVisibleRows && (
                <>
                    <Box
                        borderWidth="1px"
                        borderColor="border.default"
                        borderRadius="xl"
                        overflow="hidden"
                    >
                        <Table
                            size="sm"
                            variant="simple"
                            sx={{
                                'th, td': { px: 3 },
                                th: { py: 3, fontSize: '0.6875rem', letterSpacing: '0.05em' },
                                td: { py: 3, borderColor: 'border.subtle' },
                                'tbody tr:last-of-type td': { borderBottomWidth: 0 },
                            }}
                        >
                            <Thead>
                                <Tr>
                                    <Th w="96px">Type</Th>
                                    <Th w="96px">Date</Th>
                                    <Th w="96px">RO #</Th>
                                    <Th>Business Line</Th>
                                    <Th w="72px" isNumeric>
                                        Change
                                    </Th>
                                    <Th w="80px" isNumeric whiteSpace="nowrap">
                                        In stock
                                    </Th>
                                    <Th w="96px" isNumeric>
                                        Cost
                                    </Th>
                                    <Th w="104px">User</Th>
                                    <Th w="80px" textAlign="right">
                                        Actions
                                    </Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {visiblePendingCreates.map((draft) => {
                                    const meta = getAdjustmentTypeMeta(draft.type);
                                    const note = effectivePendingNote(draft);
                                    const isNoteOpen =
                                        expandedNote?.kind === 'pending' &&
                                        expandedNote.clientId === draft.clientId;
                                    const toggleNote = () => {
                                        if (isNoteOpen) {
                                            closeNoteEditor();
                                        } else {
                                            openPendingNote(draft);
                                        }
                                    };
                                    return (
                                        <Fragment key={draft.clientId}>
                                            <Tr bg="brand.subtle">
                                                <Td>
                                                    <Badge colorScheme={meta.colorScheme} fontSize="xs">
                                                        {meta.label}
                                                    </Badge>
                                                </Td>
                                                <Td>
                                                    <Text fontSize="sm" color="fg.muted" whiteSpace="nowrap">
                                                        Not saved
                                                    </Text>
                                                </Td>
                                                <Td>{renderRoCell(draft.roNumber)}</Td>
                                                <Td maxW={0}>
                                                    {renderBusinessLineCell(draft.businessLine)}
                                                </Td>
                                                <Td isNumeric>
                                                    {renderChange(draft.type, draft.quantity)}
                                                </Td>
                                                <Td isNumeric>—</Td>
                                                <Td isNumeric>{formatUnitCost(draft)}</Td>
                                                <Td>—</Td>
                                                <Td>
                                                    <HStack spacing={0} justify="flex-end">
                                                        {renderNoteCell(
                                                            note.length > 0,
                                                            isNoteOpen,
                                                            toggleNote,
                                                        )}
                                                        {renderDeleteCell({
                                                            show: true,
                                                            onClick: () =>
                                                                discardPendingCreate(
                                                                    draft.clientId,
                                                                ),
                                                            label: 'Discard',
                                                        })}
                                                    </HStack>
                                                </Td>
                                            </Tr>
                                            {isNoteOpen && (
                                                <Tr bg="brand.subtle">
                                                    <Td colSpan={colCount} py={2}>
                                                        <StockAdjustmentNoteEditor
                                                            value={noteDraft}
                                                            onChange={setNoteDraft}
                                                            onDone={handleNoteDone}
                                                            onCancel={handleNoteCancel}
                                                        />
                                                    </Td>
                                                </Tr>
                                            )}
                                        </Fragment>
                                    );
                                })}
                                {adjustments.map((adj, index) => {
                                    if (!matchesFilter(adj.type)) return null;
                                    const meta = getAdjustmentTypeMeta(adj.type);
                                    const isPendingDelete = pendingDeleteIds.includes(adj._id);
                                    const showDelete = canRevealDeleteAction(
                                        index,
                                        adjustments,
                                        pendingDeleteIds,
                                    );
                                    const actorName = getActorName(adj);
                                    const note = effectivePersistedNote(adj);
                                    const isNoteOpen =
                                        expandedNote?.kind === 'persisted' &&
                                        expandedNote.adjustmentId === adj._id;
                                    const toggleNote = () => {
                                        if (isNoteOpen) {
                                            closeNoteEditor();
                                        } else {
                                            openPersistedNote(adj);
                                        }
                                    };
                                    return (
                                        <Fragment key={adj._id}>
                                            <Tr
                                                opacity={isPendingDelete ? 0.55 : 1}
                                                textDecoration={
                                                    isPendingDelete ? 'line-through' : undefined
                                                }
                                            >
                                                <Td>
                                                    <Badge colorScheme={meta.colorScheme} fontSize="xs">
                                                        {meta.label}
                                                    </Badge>
                                                </Td>
                                                <Td whiteSpace="nowrap">
                                                    <Text fontSize="sm" color="fg.default">
                                                        {moment(adj.timestamp).format('MMM D')}
                                                    </Text>
                                                    <Text fontSize="xs" color="fg.muted">
                                                        {moment(adj.timestamp).format('h:mm A')}
                                                    </Text>
                                                </Td>
                                                <Td>{renderRoCell(adj.roNumber)}</Td>
                                                <Td maxW={0}>
                                                    {renderBusinessLineCell(adj.businessLine)}
                                                </Td>
                                                <Td isNumeric>
                                                    {renderChange(adj.type, adj.quantity)}
                                                </Td>
                                                <Td isNumeric>{adj.inStockQuantityAfter}</Td>
                                                <Td isNumeric>{formatUnitCost(adj)}</Td>
                                                <Td>
                                                    <Tooltip label={actorName} placement="top">
                                                        <Text fontSize="sm" noOfLines={1}>
                                                            {formatShortActorName(adj)}
                                                        </Text>
                                                    </Tooltip>
                                                </Td>
                                                <Td>
                                                    <HStack spacing={0} justify="flex-end">
                                                        {renderNoteCell(
                                                            note.length > 0,
                                                            isNoteOpen,
                                                            toggleNote,
                                                        )}
                                                        {renderDeleteCell({
                                                            show: showDelete,
                                                            isUndo: isPendingDelete,
                                                            onClick: () =>
                                                                togglePendingDelete(adj._id),
                                                            label: isPendingDelete
                                                                ? 'Undo delete'
                                                                : 'Delete',
                                                        })}
                                                    </HStack>
                                                </Td>
                                            </Tr>
                                            {isNoteOpen && (
                                                <Tr>
                                                    <Td colSpan={colCount} py={2}>
                                                        <StockAdjustmentNoteEditor
                                                            value={noteDraft}
                                                            onChange={setNoteDraft}
                                                            onDone={handleNoteDone}
                                                            onCancel={handleNoteCancel}
                                                        />
                                                    </Td>
                                                </Tr>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </Tbody>
                        </Table>
                    </Box>
                    {canExpand && (
                        <Flex justify="center" mt={2}>
                            <Button
                                size="sm"
                                variant="ghost"
                                colorScheme="gray"
                                rightIcon={
                                    <Icon
                                        name={expanded ? 'chevronUp' : 'chevronDown'}
                                        size="sm"
                                    />
                                }
                                onClick={() => setExpanded((prev) => !prev)}
                            >
                                {expanded ? 'Show less' : `Show all (${totalCount})`}
                            </Button>
                        </Flex>
                    )}
                </>
            )}
        </Box>
    );
};

export default InventoryStockAdjustmentsSection;
